import React from 'react';
import { type FieldError } from 'react-hook-form';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import type { MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import classNames from 'classnames';
import sortBy from 'lodash/sortBy';
import useCampaignSearch from '@/lib/hooks/swr/useCampaignSearch';
import useStatefulId from '@/lib/hooks/useStatefulId';
import translate from '@/lib/intl/translate';
import type DropdownOption from '@/types/DropdownOption';
import type ArrayIfMulti from '@/types/helpers/ArrayIfMulti';
import type SingleOrMultiSelectProps from '@/types/SingleOrMultiSelectProps';
import { formatCampaignForTextSelect } from '@/utils/campaigns';

const MENU_PROPS: Partial<MenuProps> = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  classes: { paper: 'max-h-96' },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
};

type CampaignSelectProps<IsMulti extends boolean> = SingleOrMultiSelectProps<
  string,
  IsMulti
> & {
  errors?: FieldError;
  omittedCampaigns?: string[];
};

function CampaignSelect<IsMulti extends boolean = false>({
  className,
  disabled,
  errors,
  id,
  multiple,
  omittedCampaigns = [],
  onChange,
  required,
  value,
}: CampaignSelectProps<IsMulti>) {
  const statefulId = useStatefulId(id);

  const { data } = useCampaignSearch({ pageSize: 1000 });

  const options = React.useMemo(
    () =>
      sortBy(
        (data?.campaigns || []).reduce<DropdownOption[]>(
          (arr, campaign) =>
            omittedCampaigns.includes(campaign.campaignId)
              ? arr
              : [formatCampaignForTextSelect(campaign), ...arr],
          [],
        ),
        ['text'],
      ),
    [data, omittedCampaigns],
  );

  const handleChange = React.useCallback(
    (event: SelectChangeEvent<ArrayIfMulti<string, IsMulti>>) => {
      onChange(event.target.value as ArrayIfMulti<string, IsMulti>);
    },
    [onChange],
  );

  return (
    <FormControl className={classNames(className, 'w-full')} size="small">
      <InputLabel id={`${statefulId}-label`} required={required}>
        {translate('CAMPAIGN')}
      </InputLabel>
      <Select
        disabled={disabled || !data}
        MenuProps={MENU_PROPS}
        error={!!errors}
        id={statefulId}
        label={translate('CAMPAIGN')}
        labelId={`${statefulId}-label`}
        multiple={multiple}
        onChange={handleChange}
        required={required}
        renderValue={(selected) => {
          if (multiple) {
            const match = options.filter((option) =>
              selected.includes(option.value),
            );

            return (
              <div className="max-w-full truncate">
                {match.map((item: DropdownOption, i) => (
                  <div key={item.value} className="inline-flex items-center">
                    {item.text}
                    {i < selected.length - 1 ? ', ' : ''}
                  </div>
                ))}
              </div>
            );
          }
          const match = options.find((option) => selected === option.value);
          return <div className="max-w-full truncate">{match?.text}</div>;
        }}
        value={value}
      >
        {!required && !multiple && (
          <MenuItem value="">{translate('NONE')}</MenuItem>
        )}
        {options.map((item) => (
          <MenuItem
            className="flex w-full items-center"
            key={item.value}
            value={item.value}
          >
            {multiple && (
              <Checkbox
                checked={value.includes(item.value)}
                className="py-2 pl-0"
              />
            )}
            {item.text}
          </MenuItem>
        ))}
      </Select>
      {!!errors && <FormHelperText error>{errors.message}</FormHelperText>}
    </FormControl>
  );
}

export default CampaignSelect;
